import { yupResolver } from '@hookform/resolvers/yup';
import { useRegister } from 'api-hooks/auth/mutation';
import {
  GenderEnum,
  OtpEnum,
  PassengerIdentifierTypeEnum,
} from 'api-hooks/common/model';
import NavigationEnum from 'common/navigation';
import Text from 'components/commons/text';
import Verification from 'components/commons/verification';
import Button from 'components/elements/button';
import Checkbox from 'components/elements/checkbox/checkbox';
import Input from 'components/elements/field';
import Form from 'components/elements/form';
import { AuthType, useAuthorization } from 'hooks/use-authorization';
import GenderSelectInput from 'modules/select-inputs/gender';
import PassengerIdentifierSelectInput from 'modules/select-inputs/passenger-identifier-type';
import Link from 'next/link';
import { useRouter } from 'next/router';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { useToasts } from 'react-toast-notifications';

import { RegisterFormType, registerFormType } from './form-type';
import { ALinkMenu, ALinkTerm, CardContainer, LoginContainer } from './style';

interface Props {
  step: number;
  setStep: React.Dispatch<React.SetStateAction<number>>;
}

export default function Register(props: Props) {
  const { t } = useTranslation();
  const [agreeCheckbox, setAgreeCheckbox] = React.useState(false);
  const { step, setStep } = props;
  const router = useRouter();
  const { mutateAsync: mutateRegister } = useRegister();
  const toast = useToasts();
  useAuthorization({
    type: AuthType.UnAuthenticated,
  });

  const defaultValues = React.useMemo<RegisterFormType>(() => {
    return {
      name: '',
      email: '',
      password: '',
      passwordConfirmation: '',
      identifierCode: '',
      identifierType: PassengerIdentifierTypeEnum.NationalId,
      gender: GenderEnum.Male,
      verificationToken: '',
    };
  }, []);

  const resolver = yupResolver(registerFormType(t));
  const methods = useForm<RegisterFormType>({
    defaultValues,
    resolver,
    mode: 'onChange',
  });
  const { isValid } = methods.formState;

  const onSubmit = React.useCallback(
    async (values: RegisterFormType) => {
      try {
        await mutateRegister(values);
        await router.push(NavigationEnum.login);
      } catch (e) {
        toast.addToast(e?.message, { appearance: 'error', autoDismiss: true });
        setStep(1);
      }
    },
    [mutateRegister, router, setStep, toast],
  );

  return (
    <CardContainer>
      {step === 1 && (
        <>
          <Text
            variant="headline3"
            weight="bold"
            align="center"
            css={{ mb: 16 }}
          >
            {t('common:register_account')}
          </Text>
          <Text variant="body3" css={{ mb: 40 }} align="center">
            {t('common:register_account_message')}
          </Text>
        </>
      )}
      <Form methods={methods} onSubmit={onSubmit} autoComplete="off">
        {step === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Input
              type="text"
              name="name"
              label={t('common:full_name')}
              placeholder={t('common:input_full_name')}
              autoComplete="full-name-nope"
              required
              hint={t('common:enter_name_according_to_identity')}
            />
            <GenderSelectInput
              name="gender"
              label={t('common:gender')}
              placeholder={t('common:select_gender')}
              required
            />
            <PassengerIdentifierSelectInput
              name="identifierType"
              label={t('common:identity_type')}
              placeholder={t('common:select_identity_type')}
              onChange={() => methods.setValue('identifierCode', '')}
              required
            />
            <Input
              type="text"
              name="identifierCode"
              label={t('common:identity_number')}
              placeholder={t('common:input_identity_number')}
              required
            />
            <Input
              type="email"
              name="email"
              label={t('common:email_address')}
              placeholder={t('common:input_email_address')}
              required
              autoComplete="email-off"
            />
            <Input
              type="password"
              name="password"
              label={t('common:password')}
              placeholder={t('common:input_password')}
              required
              autoComplete="password-off"
              hint={t('common:min_8_character')}
            />
            <Input
              type="password"
              name="passwordConfirmation"
              label={t('common:password_confirmation')}
              placeholder={t('common:input_password')}
              required
              autoComplete="confirm-password-off"
            />
            <Checkbox
              isSelected={agreeCheckbox}
              onChange={(val) => setAgreeCheckbox(val)}
              name=""
              label={
                <Trans
                  i18nKey="common:register_agree_notice"
                  components={{
                    anchor: (
                      <ALinkTerm href="/term-and-condition" target="_blank" />
                    ),
                  }}
                />
              }
              required
            />
            <Button
              disabled={!agreeCheckbox || !isValid}
              onClick={() => setStep(2)}
              css={{ width: '100%' }}
            >
              {t('common:register_account')}
            </Button>
            <LoginContainer>
              <Text variant="body3">{t('common:already_have_an_account')}</Text>
              <Link href={NavigationEnum.login} passHref>
                <ALinkMenu href={NavigationEnum.login}>
                  {t('common:login_now')}
                </ALinkMenu>
              </Link>
            </LoginContainer>
          </div>
        )}
        {step === 2 && (
          <Verification
            email={methods.getValues('email')}
            setStep={() => setStep(1)}
            onSubmit={(verificationToken) => {
              methods.setValue('verificationToken', verificationToken, {
                shouldTouch: true,
                shouldValidate: true,
              });
              methods.handleSubmit(onSubmit)();
            }}
            type={OtpEnum.Register}
          />
        )}
      </Form>
    </CardContainer>
  );
}
